import { Box, Typography, useTheme } from '@mui/material';
import { addDays, format, startOfDay, startOfHour } from 'date-fns';
import { useMemo } from 'react';
import { Area, AreaChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { Ingredient, ArchivedStoreLog } from '../../../apiTypes';
import { StyledPaper } from '../../../constants/styles';
import { t } from '../../../constants/translations';
import { useAdmin } from '../../../contexts/admin.context';
import { useCafe } from '../../../contexts/cafe.context';
import { Lang, useLocale } from '../../../contexts/locale.context';
import { dateLocaleMap } from '../../../utils/dates';
import getQntLabel from '../../../utils/getQntLabel';
import { Map } from '../../../utils/map';
import { TimeScale } from '../Reports';
import { getReportRowsMap } from './ReportByStores';

function groupLogsByHour(logs: ArchivedStoreLog[]): Record<string, ArchivedStoreLog[]> {
  const grouped: Record<string, ArchivedStoreLog[]> = {};

  // eslint-disable-next-line no-restricted-syntax
  for (const log of logs) {
    const hour = startOfHour(log.time);
    const hourKey = hour.getTime();
    grouped[hourKey] = [...(grouped[hourKey] || []), log];
  }

  return grouped;
}

function groupLogsByDay(logs: ArchivedStoreLog[]): Record<string, ArchivedStoreLog[]> {
  const grouped: Record<string, ArchivedStoreLog[]> = {};

  // eslint-disable-next-line no-restricted-syntax
  for (const log of logs) {
    const day = startOfDay(log.time);
    const dayKey = day.getTime();

    grouped[dayKey] = [...(grouped[dayKey] || []), log];
  }

  return grouped;
}

function groupLogsByDate(logs: ArchivedStoreLog[]): Record<string, ArchivedStoreLog[]> {
  const grouped: Record<string, ArchivedStoreLog[]> = {};

  // eslint-disable-next-line no-restricted-syntax
  for (const log of logs) {
    const dayKey = format(log.time, 'dd.MM');

    grouped[dayKey] = [...(grouped[dayKey] || []), log];
  }

  return grouped;
}

function prepareWeeklyChartData(
  logs: ArchivedStoreLog[],
  id: string,
  ingredientsMap: Map<Ingredient>,
  lang: Lang,
  timeRange: [number, number]
) {
  const lastWeek = {
    start: new Date(timeRange[0]),
    end: new Date(timeRange[1]),
  };

  // const lastWeekOrders = orders.filter((order) => isWithinInterval(order.created, lastWeek));

  const groupedLogsByDay = groupLogsByDay(logs);

  const chartData = Array.from({ length: 7 }, (_, i) => {
    const dayKey = startOfDay(addDays(lastWeek.start, i)).getTime();
    const reportRowsMap = getReportRowsMap(groupedLogsByDay[dayKey] || [], ingredientsMap);

    return {
      title: format(dayKey, 'EEE', { locale: dateLocaleMap[lang] }),
      [t.supply[lang]]: reportRowsMap[id]?.added ?? 0,
      [t.semi_finished_products_production[lang]]: reportRowsMap[id]?.production ?? 0,
      [t.movement[lang]]: reportRowsMap[id]?.moved ?? 0,
      [t.inventory[lang]]: reportRowsMap[id]?.inventored ?? 0,
      [t.menu_item_production[lang]]: reportRowsMap[id]?.sold ?? 0,
    };
  });

  return chartData;
}

function prepareMonthlyChartData(
  logs: ArchivedStoreLog[],
  id: string,
  ingredientsMap: Map<Ingredient>,
  lang: Lang,
  timeRange: [number, number]
) {
  const groupedLogsByDate = groupLogsByDate(logs);

  const chartData = Array.from({ length: 31 }, (_, i) => {
    const dayKey = addDays(timeRange[0], i).getTime();
    const reportRowsMap = getReportRowsMap(groupedLogsByDate[format(dayKey, 'dd.MM')] || [], ingredientsMap);

    return {
      title: format(dayKey, 'dd.MM', { locale: dateLocaleMap[lang] }),
      [t.supply[lang]]: reportRowsMap[id]?.added ?? 0,
      [t.semi_finished_products_production[lang]]: reportRowsMap[id]?.production ?? 0,
      [t.movement[lang]]: reportRowsMap[id]?.moved ?? 0,
      [t.inventory[lang]]: reportRowsMap[id]?.inventored ?? 0,
      [t.menu_item_production[lang]]: reportRowsMap[id]?.sold ?? 0,
    };
  });

  return chartData;
}

function prepareChartData(logs: ArchivedStoreLog[], id: string, ingredientsMap: Map<Ingredient>, lang: Lang) {
  const groupedByHours = groupLogsByHour(logs);

  const chartData = Array.from({ length: 24 }, (_, i) => {
    const hourKey = startOfHour(new Date(logs[0]?.time || 0).setHours(i)).getTime();
    const reportRowsMap = getReportRowsMap(groupedByHours[hourKey] || [], ingredientsMap);
    return {
      title: format(hourKey, 'HH:mm'),
      [t.supply[lang]]: reportRowsMap[id]?.added ?? 0,
      [t.semi_finished_products_production[lang]]: reportRowsMap[id]?.production ?? 0,
      [t.movement[lang]]: reportRowsMap[id]?.moved ?? 0,
      [t.inventory[lang]]: reportRowsMap[id]?.inventored ?? 0,
      [t.menu_item_production[lang]]: reportRowsMap[id]?.sold ?? 0,
    };
  });

  return chartData;
}

type Props = {
  ingredientId: string;
  timeScale: TimeScale;
  logs: ArchivedStoreLog[];
  timeRange: [number, number];
};

const IngredientChart = ({ timeScale, logs = [], ingredientId, timeRange }: Props) => {
  const { lang } = useLocale();
  const { ingredientsMap } = useCafe();
  const { getCurrency } = useAdmin();
  const { palette } = useTheme();

  const data = useMemo(() => {
    if (timeScale === 'day') {
      return prepareChartData(logs, ingredientId, ingredientsMap, lang);
    }

    if (timeScale === 'week') {
      return prepareWeeklyChartData(logs, ingredientId, ingredientsMap, lang, timeRange);
    }
    return prepareMonthlyChartData(logs, ingredientId, ingredientsMap, lang, timeRange);
  }, [ingredientId, ingredientsMap, lang, logs, timeScale, timeRange]);

  return (
    <StyledPaper isCard sx={{ p: 2, width: 'auto', my: 4 }}>
      <Box sx={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {logs.length === 0 && (
          <Typography color={(t) => t.palette.text.secondary}>{t.there_is_nothing[lang]}</Typography>
        )}

        {logs.length > 0 && (
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data}>
              <defs>
                <linearGradient id="supply" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={palette.success.main} stopOpacity={0.5} />
                  <stop offset="100%" stopColor={palette.background.paper} stopOpacity={0} />
                </linearGradient>
                <linearGradient id="semi_finished_products_production" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={palette.primary.main} stopOpacity={0.9} />
                  <stop offset="100%" stopColor={palette.background.paper} stopOpacity={0.6} />
                </linearGradient>
                <linearGradient id="movement" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={palette.grey[600]} stopOpacity={0.5} />
                  <stop offset="100%" stopColor={palette.background.paper} stopOpacity={0} />
                </linearGradient>
                <linearGradient id="inventory" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={palette.error.main} stopOpacity={0.5} />
                  <stop offset="100%" stopColor={palette.background.paper} stopOpacity={0} />
                </linearGradient>
                <linearGradient id="menu_item_production" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={palette.secondary.main} stopOpacity={0.5} />
                  <stop offset="100%" stopColor={palette.background.paper} stopOpacity={0} />
                </linearGradient>
              </defs>

              <XAxis dataKey="title" />
              <YAxis />

              <Tooltip<string, string>
                formatter={(value) => (value ? getQntLabel(Number(value), ingredientsMap[ingredientId], lang) : '')}
                labelFormatter={(_, p) => p?.[0]?.payload?.title}
                contentStyle={{ borderRadius: '8px' }}
              />
              <Area
                type="monotone"
                dataKey={t.supply[lang]}
                stroke={palette.success.main}
                dot={false}
                activeDot={{ r: 2 }}
                fillOpacity={1}
                fill="url(#supply)"
              />
              <Area
                type="monotone"
                dataKey={t.semi_finished_products_production[lang]}
                stroke={palette.primary.main}
                strokeWidth={2}
                dot={false}
                fillOpacity={1}
                fill="url(#semi_finished_products_production)"
              />
              <Area
                type="monotone"
                dataKey={t.movement[lang]}
                stroke={palette.grey[600]}
                strokeWidth={2}
                dot={false}
                fillOpacity={1}
                fill="url(#movement)"
              />
              <Area
                type="monotone"
                dataKey={t.inventory[lang]}
                stroke={palette.error.main}
                strokeWidth={2}
                dot={false}
                fillOpacity={1}
                fill="url(#inventory)"
              />
              <Area
                type="monotone"
                dataKey={t.menu_item_production[lang]}
                stroke={palette.secondary.main}
                strokeWidth={2}
                dot={false}
                fillOpacity={1}
                fill="url(#menu_item_production)"
              />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </Box>
    </StyledPaper>
  );
};

export default IngredientChart;
